package com.vizio.redwolf.consent.api

import com.vizio.redwolf.consent.client.ConsentV2FusedHttpClient
import com.vizio.redwolf.consent.model.ConsentClientData
import com.vizio.redwolf.consent.model.ConsentClientResponse
import com.vizio.redwolf.consent.model.ConsentContent
import com.vizio.redwolf.consent.model.ConsentFailureMessage
import com.vizio.redwolf.consent.model.ConsentMessageFormat
import com.vizio.redwolf.consent.model.ConsentReceiptResponse
import com.vizio.redwolf.consent.model.ConsentV2DataResponse
import com.vizio.redwolf.consent.model.ResourceInfo
import com.vizio.redwolf.consent.model.SaveUserConsentV2ProxyRequestInfo
import com.vizio.redwolf.consent.model.UserConsentV2DataRequestInfo
import com.vizio.redwolf.consent.model.UserConsentV2ProxyStatusRequestInfo
import com.vizio.redwolf.consent.model.UserConsentV2Response
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.ConsentV2FusedEnvironment
import com.vizio.redwolf.utils.ConsentV2FusedEnvironmentConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.TimeoutException
import com.vizio.redwolf.utils.URLRequestHttpClient
import com.vizio.redwolf.utils.chooseConsentV2FusedEnvironment
import com.vizio.redwolf.utils.clientprovider.ClientConfigFactory
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import com.vizio.redwolf.utils.model.ConnectionConfig
import io.ktor.client.call.body
import io.ktor.client.statement.HttpResponse
import io.ktor.http.isSuccess

class ConsentV2FusedApi(
    consentEnv: ConsentV2FusedEnvironment,
    private val appConfig: AppConfig,
    private val clientConfig: ClientConfig = ClientConfigFactory.getClientConfig(WebApiUseCase.SDK_HEADERS)
) : ConsentV2FusedApiInterface {

    constructor(envConfig: ConsentV2FusedEnvironmentConfig, appConfig: AppConfig) : this(chooseConsentV2FusedEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.SDK_HEADERS))
    constructor(envConfig: ConsentV2FusedEnvironmentConfig, appConfig: AppConfig, timeout: Long) : this(chooseConsentV2FusedEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.SDK_HEADERS, timeout))

    private val consentV2Client = ConsentV2FusedHttpClient(consentEnv = consentEnv, appConfig = appConfig, clientConfig = clientConfig)
    private val urlRequestClient by lazy {
        val basicClientProvider = ClientConfigFactory.getClientConfig(WebApiUseCase.BASIC).clientProvider
        URLRequestHttpClient(clientProvider = basicClientProvider, clientConfig = clientConfig)
    }

    /**
     * Request consent data for a given list of consent id's/group of consent id's
     * @param requestInfo UserConsentDataRequestInfo
     *
     * @return ConsentDataResponse on success
     */
    override suspend fun requestConsentData(requestInfo: UserConsentV2DataRequestInfo, connectionConfig: ConnectionConfig?, traceId: String?): ConsentResult<ConsentClientResponse> {
        return retryConsentRequest(connectionConfig?.retryConfig) {
            val consentDataResponse = consentV2Client.getConsentData(requestInfo, traceId)
            val consentResult: ConsentResult<ConsentV2DataResponse> = handleConsentHttpResult(consentDataResponse)
            adaptClientResponse(consentResult, requestInfo)
        }
    }

    override suspend fun requestUserConsentStatus(requestInfo: UserConsentV2ProxyStatusRequestInfo, connectionConfig: ConnectionConfig?, traceId: String?): ConsentResult<UserConsentV2Response> {
        return retryConsentRequest(connectionConfig?.retryConfig) {
            val userConsentResponse = consentV2Client.getUserConsentStatus(requestInfo, traceId)
            handleConsentHttpResult(userConsentResponse)
        }
    }

    override suspend fun saveUserConsent(requestInfo: SaveUserConsentV2ProxyRequestInfo, connectionConfig: ConnectionConfig?, traceId: String?): ConsentResult<ConsentReceiptResponse> {
        return retryConsentRequest(connectionConfig?.retryConfig) {
            val consentReceiptResponse = consentV2Client.saveUserConsent(requestInfo, traceId)
            handleConsentHttpResult(consentReceiptResponse)
        }
    }

    private suspend fun adaptClientResponse(
        consentResult: ConsentResult<ConsentV2DataResponse>,
        requestInfo: UserConsentV2DataRequestInfo
    ): ConsentResult<ConsentClientResponse> {
        return when (consentResult) {
            is ConsentResult.Success -> {
                val consents = consentResult.data.consents
                if (consents != null) {
                    return when (requestInfo.consentMessageFormat) {
                        ConsentMessageFormat.TEXT -> createClientResponseText(consentResult, requestInfo.expand)
                        ConsentMessageFormat.HTML_CSS -> createClientResponseHtmlCss(consentResult, requestInfo.expand)
                        else -> ConsentResult.Failed(consentResult.headers, ERROR_INVALID_CONSENT_MESSAGE_FORMAT)
                    }
                }
                val clientResponse = ConsentClientResponse(
                    consentClientData = null,
                    consentGroups = consentResult.data.consentGroups,
                    consentActions = consentResult.data.consentActions,
                    consentToken = consentResult.data.consentToken
                )
                ConsentResult.Success(consentResult.headers, clientResponse)
            }

            is ConsentResult.Failed -> {
                consentResult
            }

            is ConsentResult.Error -> {
                consentResult
            }

            is ConsentResult.Timeout -> {
                consentResult
            }
        }
    }

    private suspend fun createClientResponseText(
        consentResult: ConsentResult.Success<ConsentV2DataResponse>,
        expand: Boolean
    ): ConsentResult<ConsentClientResponse> {
        return consentResult.data.consents?.map { consent ->
            val textUrl = consent.textUrl
            if (textUrl.isNullOrEmpty()) {
                return ConsentResult.Failed(consentResult.headers, URL_ERROR)
            } else {
                val urlContent = retrieveUrlContent(url = textUrl)
                if (urlContent == URL_ERROR) {
                    return ConsentResult.Failed(consentResult.headers, URL_ERROR)
                }
                val resourceInfo = packageUrlContent(url = textUrl, urlContent = urlContent, expand = expand)

                ConsentClientData(
                    id = consent.id,
                    version = consent.version,
                    title = consent.title,
                    language = consent.language,
                    consentContent = ConsentContent.Text(textInfo = resourceInfo)
                )
            }
        }?.let { clientDataList ->
            // Creating client response
            val clientResponse = ConsentClientResponse(
                consentClientData = clientDataList,
                consentGroups = consentResult.data.consentGroups,
                consentActions = consentResult.data.consentActions,
                consentToken = consentResult.data.consentToken
            )
            ConsentResult.Success(consentResult.headers, clientResponse)
        } ?: ConsentResult.Failed(consentResult.headers, CONSENT_RETRIEVAL_ERROR)
    }

    private suspend fun createClientResponseHtmlCss(
        consentResult: ConsentResult.Success<ConsentV2DataResponse>,
        expand: Boolean
    ): ConsentResult<ConsentClientResponse> {
        return consentResult.data.consents?.map { consent ->
            val htmlUrl = consent.htmlUrl
            val cssUrls = consent.cssUrls
            if (htmlUrl.isNullOrEmpty()) {
                return ConsentResult.Failed(consentResult.headers, URL_ERROR)
            } else {
                val urlContent = retrieveUrlContent(url = htmlUrl)
                if (urlContent == URL_ERROR) {
                    return ConsentResult.Failed(consentResult.headers, URL_ERROR)
                }
                val resourceInfo = packageUrlContent(url = htmlUrl, urlContent = urlContent, expand = expand)
                val cssContentList = retrieveCssContent(cssUrls = cssUrls, expand = expand)
                if (checkUrlListContainsError(cssContentList)) {
                    return ConsentResult.Failed(consentResult.headers, URL_ERROR)
                }
                val cssInfo = packageUrlContentList(cssUrls, cssContentList, expand)

                ConsentClientData(
                    id = consent.id,
                    version = consent.version,
                    title = consent.title,
                    language = consent.language,
                    consentContent = ConsentContent.HtmlCss(htmlInfo = resourceInfo, cssInfo = cssInfo)
                )
            }
        }?.let { clientDataList ->
            // Creating client response
            val clientResponse = ConsentClientResponse(
                consentClientData = clientDataList,
                consentGroups = consentResult.data.consentGroups,
                consentActions = consentResult.data.consentActions,
                consentToken = consentResult.data.consentToken
            )
            ConsentResult.Success(consentResult.headers, clientResponse)
        } ?: ConsentResult.Failed(consentResult.headers, CONSENT_RETRIEVAL_ERROR)
    }

    private suspend fun retrieveUrlContent(url: String): String {
        val urlContent = urlRequestClient.getDataFromUrl(url)
        return handleUrlRequestResult(urlContent)
    }

    private suspend fun retrieveCssContent(cssUrls: List<String>, expand: Boolean): List<String> {
        return if (expand) {
            cssUrls.map { cssUrl ->
                retrieveUrlContent(cssUrl)
            }
        } else {
            emptyList()
        }
    }

    private fun checkUrlListContainsError(urlContentList: List<String>): Boolean {
        for (urlContent in urlContentList) {
            if (urlContent == URL_ERROR) return true
        }
        return false
    }

    private fun packageUrlContent(url: String, urlContent: String, expand: Boolean): ResourceInfo {
        return if (expand) {
            ResourceInfo(url = url, content = urlContent)
        } else {
            ResourceInfo(url = url, content = null)
        }
    }

    private fun packageUrlContentList(urlList: List<String>, urlContentList: List<String>, expand: Boolean): List<ResourceInfo> {
        return if (expand) {
            urlList.zip(urlContentList) { url, urlContent -> ResourceInfo(url = url, content = urlContent) }
        } else {
            urlList.map { url -> ResourceInfo(url = url, content = null) }
        }
    }

    private inline fun handleUrlRequestResult(httpResult: HttpResult<String>): String {
        return when (httpResult) {
            is HttpResult.Success -> httpResult.data
            is HttpResult.Error -> URL_ERROR
        }
    }

    private suspend inline fun <T : HttpResponse, reified R : Any> handleConsentHttpResult(httpResult: HttpResult<T>): ConsentResult<R> {
        return when (httpResult) {
            is HttpResult.Success -> {
                val httpResponse: HttpResponse = httpResult.data
                val statusCode = httpResult.data.status.value
                val headers = ResponseHeaders.Valid(statusCode)
                if (httpResponse.status.isSuccess()) {
                    try {
                        val response = httpResponse.body<R>()
                        ConsentResult.Success(headers, response)
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        ConsentResult.Error(headers, ex)
                    }
                } else {
                    try {
                        val consentFailureMessage = httpResponse.body<ConsentFailureMessage>()
                        ConsentResult.Failed(headers, consentFailureMessage.message)
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        ConsentResult.Error(headers, ex)
                    }
                }
            }

            is HttpResult.Error -> {
                val headers = ResponseHeaders.Invalid
                val exception = httpResult.exception
                if (exception is TimeoutException)
                    ConsentResult.Timeout(headers, exception)
                else
                    ConsentResult.Error(headers, exception)
            }
        }
    }

    companion object {
        const val LANGUAGE_US = "en-us"
        const val LANGUAGE_SPANISH = "es-mx"
        const val LANGUAGE_FRENCH = "fr-ca"
        private const val URL_ERROR = "Invalid Url; Unable to get Url content"
        private const val CONSENT_RETRIEVAL_ERROR = "Could not get consents"
        private const val ERROR_INVALID_CONSENT_MESSAGE_FORMAT = "Invalid consent message format"
    }
}